import { defineStore } from "pinia"
import axios from "axios"
import { cloneDeep, forEach } from "lodash-es"
import type { CreateProfile, Profile } from "@/interfaces"
import { UPDATE_DATA_THRESHOLD } from "@/constants"
import { manufacturerStore, userStore } from "@/store"
import type { OptimisticAddOptionInterface, OptimisticUpdateOptionInterface } from "@/interfaces"
import { materialStore } from "."
import { getId, paginatedFetch, optimisticAdd, optimisticUpdate, optimisticDelete, addOrUpdate, bulkAddOrUpdate } from "@/libraries/helpers"
import { getCache, setCache } from "@/libraries/helpers"

const cacheKey = "profiles"

const mapData = (profile: Profile) => ({
  ...profile,
  material: materialStore.all.find(material => material.id === getId(profile.material)),
})

export default defineStore("profile", {
  state: () => ({
    all: [],
    manufacturerProfiles: [],
  }),
  getters: {
    url: () => `/v1/profiles`,
    mappedData: state => state.all.map(mapData),
    mappedManufacturerProfiles: state => state.manufacturerProfiles.map(mapData)
  },
  actions: {
    init() {
      this.all = (getCache(cacheKey)?.data || []) as Profile[]
      this.manufacturerProfiles = (getCache(`${cacheKey}-manufacturer`)?.data ||
        []) as Profile[]
    },
    modifyData(data = []) {
      const isSameManufacturer = userStore.mappedCurrent.availableManufacturers?.includes(manufacturerStore.manufacturerNickname)
      const objects = [cloneDeep(this.all)]
      
      if (isSameManufacturer) objects.push(cloneDeep(this.manufacturerProfiles))
      const updatedObjects = bulkAddOrUpdate(objects, data)

      this.all = updatedObjects[0]
      if (isSameManufacturer) this.manufacturerProfiles = updatedObjects[1]
    },
    async fetchAll({ persist = false, prefetching = false } = {}) {
      await paginatedFetch({
        url: "/v1/profiles",
        persist,
        prefetching,
        callback: data => forEach(data || [], v => addOrUpdate(this.all, v, ["id"])),
        runCallbackCondition: data =>
          this.all.length === 0 || data.length > UPDATE_DATA_THRESHOLD,
      })
      setCache(cacheKey, this.all)
    },
    async fetchByManufacturerId(
      manufacturerId: number,
      { persist = false, prefetching = false } = {}
    ) {
      await paginatedFetch({
        url: `/v1/manufacturers/${manufacturerId}/profiles`,
        persist,
        prefetching,
        callback: data => forEach(data || [], v => addOrUpdate(this.all, v, ["id"])),
        runCallbackCondition: data =>
          this.all.length === 0 || data.length > UPDATE_DATA_THRESHOLD,
      })
      setCache(cacheKey, this.all)
    },
    async fetchManufacturerProfiles({ persist = false, prefetching = false } = {}) {
      const manufacturerId = manufacturerStore.current?.id
      if (!manufacturerId) return console.error("No manufacturer detected")
      await paginatedFetch({
        url: `/v1/manufacturers/${manufacturerId}/profiles`,
        persist,
        prefetching,
        callback: data => forEach(data || [], v => addOrUpdate(this.manufacturerProfiles, v, ["id"])),
        runCallbackCondition: data =>
          this.manufacturerProfiles.length === 0 || data.length > UPDATE_DATA_THRESHOLD,
      })
      setCache(`${cacheKey}-manufacturer`, this.manufacturerProfiles)
    },
    add(values: CreateProfile, options: OptimisticAddOptionInterface = {}) {
      values.material = getId(values.material)
      const allObjects = [this.all]
      if (
        userStore.mappedCurrent.availableManufacturers?.includes(
          manufacturerStore.manufacturerNickname
        )
      )
        allObjects.push(this.manufacturerProfiles)
      return optimisticAdd({
        allObjects,
        values,
        url: "/v1/profiles",
        onSuccess: () => {
          if (!options?.withoutOptimistic) setCache(cacheKey, this.all)
        },
        ...options
      })
    },
    update(values: Profile, options: OptimisticUpdateOptionInterface = {}) {
      values.material = getId(values.material)
      const allObjects = [this.all]
      if (
        userStore.mappedCurrent.availableManufacturers?.includes(
          manufacturerStore.manufacturerNickname
        )
      )
        allObjects.push(this.manufacturerProfiles)
      return optimisticUpdate({
        allObjects,
        values,
        url: `/v1/profiles/${values.id}`,
        onSuccess: () => {
          if (!options?.withoutOptimistic) setCache(cacheKey, this.all)
        },
        ...options
      })
    },
    remove(id: number) {
      const allObjects = [this.all]
      if (
        userStore.mappedCurrent.availableManufacturers?.includes(
          manufacturerStore.manufacturerNickname
        )
      )
        allObjects.push(this.manufacturerProfiles)
      return optimisticDelete({
        allObjects,
        url: `/v1/profiles/${id}`,
        id,
        onSuccess: () => setCache(cacheKey, this.all),
      })
    },
    getImage(id: string | number) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/v1/profiles/${id}/images`)
          .then(({ data }) => resolve(data))
          .catch(reject)
      })
    },
  },
})
